import { useEffect, useState, useRef } from "react";
import { CINEMATIC_SLIDES } from "../../lib/data";

/**
 * CinematicSlider — full-bleed auto-advancing gallery using ESTATE / LIFESTYLE
 * imagery only (drone shots, amenity AI imagery). No villa close-ups.
 */
export default function CinematicSlider() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (paused) return;
    intervalRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % CINEMATIC_SLIDES.length);
    }, 5500);
    return () => clearInterval(intervalRef.current);
  }, [paused]);

  const goto = (i) => {
    setIndex(((i % CINEMATIC_SLIDES.length) + CINEMATIC_SLIDES.length) % CINEMATIC_SLIDES.length);
  };

  return (
    <section
      id="gallery"
      data-testid="cinematic-slider"
      className="relative w-full bg-[#EBE7DF] overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative w-full h-[80svh] sm:h-[88svh] md:h-[92svh] overflow-hidden">
        {CINEMATIC_SLIDES.map((s, i) => (
          <div
            key={s.src}
            data-testid={`slide-${i}`}
            className={`absolute inset-0 transition-opacity duration-[1800ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${
              i === index ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
            aria-hidden={i !== index}
          >
            <img
              src={s.src}
              alt=""
              className={`w-full h-full object-cover ${
                i === index ? "scale-105" : "scale-100"
              }`}
              style={{ transition: "transform 7s ease-out" }}
              loading={i === 0 ? "eager" : "lazy"}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F0C]/55 via-transparent to-[#0A0F0C]/15" />
          </div>
        ))}

        {/* Caption — bottom-left, minimal */}
        <div className="absolute bottom-8 left-6 sm:bottom-12 sm:left-10 lg:left-16 z-20 flex items-baseline gap-6">
          <p className="font-serif-display text-[#F7F5F0]/95 italic text-lg md:text-2xl tracking-tight">
            {CINEMATIC_SLIDES[index].caption}
          </p>
        </div>

        {/* Slide index — top-right */}
        <div className="absolute top-8 right-6 sm:top-12 sm:right-10 lg:right-16 z-20 flex items-baseline gap-3 text-[#F7F5F0] font-serif-display tracking-tight">
          <span className="text-3xl md:text-4xl">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="text-sm md:text-base text-[#E2CFA1]">
            / {String(CINEMATIC_SLIDES.length).padStart(2, "0")}
          </span>
        </div>

        {/* Dot navigation */}
        <div className="absolute bottom-8 right-6 sm:bottom-12 sm:right-10 lg:right-16 z-20 flex items-center gap-3">
          {CINEMATIC_SLIDES.map((_, i) => (
            <button
              key={i}
              data-testid={`slider-dot-${i}`}
              onClick={() => goto(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-[2px] transition-all duration-500 ${
                i === index
                  ? "w-10 bg-[#E2CFA1]"
                  : "w-5 bg-[#F7F5F0]/40 hover:bg-[#F7F5F0]/70"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
